import { css } from "./tokens";

/**
 * EN-3907 (review): the theme for an antd date panel.
 *
 * antd paints a calendar in its own blue unless every selected/today/hover state is overridden.
 * `DateRangePicker` did that for itself, so the range filter looked right while the plain
 * `DatePicker` in the workflow rule builder — which only ever styled its input — kept antd's
 * default. Abe saw the two side by side.
 *
 * Shared rather than copied so a third picker cannot drift off-theme the same way. The colours
 * are the `portal-date-range-*` tenant variables, which is what makes a tenant's own palette
 * reach the calendar; `tenantStyles.ts` owns their defaults.
 *
 * Font family is deliberately included: antd sets its own on the header, the weekday row and the
 * cells, so a rule on the popup root alone leaves parts of the panel in Arial.
 */
export const datePickerPanelCss = css({
  "&, & *": {
    fontFamily: "$openSans !important",
  },

  ".ant-picker-cell": {
    "&:hover:not(.ant-picker-cell-selected):not(.ant-picker-cell-range-start):not(.ant-picker-cell-range-end)":
      {
        ".ant-picker-cell-inner": {
          border:
            "1px solid var(--colors-portal-date-range-day-hover-border) !important",
          borderRadius: "$round !important",
        },
      },
  },

  ".ant-picker-cell-in-view": {
    "&.ant-picker-cell-today": {
      ".ant-picker-cell-inner": {
        color: "var(--colors-portal-date-range-day-today-text)",
        background:
          "var(--colors-portal-date-range-day-today-background) !important",
        borderRadius: "$round !important",

        "&::before": {
          borderColor:
            "var(--colors-portal-date-range-day-hover-border) !important",
          borderRadius: "$round !important",
        },
      },
    },

    "&.ant-picker-cell-selected, &.ant-picker-cell-range-start, &.ant-picker-cell-range-end":
      {
        ".ant-picker-cell-inner": {
          background:
            "var(--colors-portal-date-range-day-selected-background) !important",
          color: "$white !important",
          borderRadius: "$round !important",
        },
      },

    // The trail joining the two ends of a range. Harmless on a single-date panel, which never
    // sets these classes.
    "&.ant-picker-cell-in-range::before, &.ant-picker-cell-range-hover::before":
      {
        background: "var(--colors-portal-date-range-trail) !important",
      },
  },

  // The bar underlining whichever end of a range is being edited is NOT in here: rc-picker
  // renders `-active-bar` as a child of the picker root, next to the two inputs, not inside the
  // popup this class is attached to. It is themed in `dateRangePickerInputCss` instead.

  // The "Today" link in the panel footer. It is an <a>, so it takes antd's link colour — blue —
  // and the first cut of this rule missed it entirely by targeting `.ant-picker-today-btn`,
  // which antd renamed to `-now-btn` (it is `.ant-picker-now-btn` as of 5.27). Both names are
  // listed, and the footer anchor is matched directly, so the rule survives the rename in
  // either direction; datePickerPanel.test.ts fails if antd renames it again.
  ".ant-picker-footer a, .ant-picker-now-btn, .ant-picker-today-btn": {
    color: "var(--colors-portal-date-range-day-today-text) !important",

    "&:hover": {
      color:
        "var(--colors-portal-date-range-day-selected-background) !important",
    },
  },

  // The month and year buttons in the panel header. antd tints them with its own primary on
  // hover, which is the last blue left once the cells and the Today link are themed.
  ".ant-picker-header-view button:hover": {
    color: "var(--colors-portal-date-range-day-selected-background) !important",
  },
});
